/*
 * AdminTrashTab — 30-day Series Trash view for High King / Admin.
 *
 * Feb 2026 user spec: soft-delete safety net. Series (audiobook + text)
 * deleted anywhere in the app land here for 30 days, after which they
 * are auto-purged by the backend on the next list request.
 *
 * Actions:
 *   Restore  — POST /api/admin/trash/:id/restore  →  puts the series
 *              and every chapter/episode back into the live collections.
 *   Permanent delete — DELETE /api/admin/trash/:id  →  purges NOW
 *              (audio files on disk are removed for audiobook entries).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RotateCcw, Trash2, RefreshCw, BookOpen, Headphones, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'react-hot-toast';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

const KindIcon = ({ kind }) => {
  if (kind === 'audiobook') return <Headphones className="w-4 h-4 text-fuchsia-300" />;
  if (kind === 'text') return <BookOpen className="w-4 h-4 text-sky-300" />;
  return <BookOpen className="w-4 h-4 text-slate-300" />;
};

const AdminTrashTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [purgedCount, setPurgedCount] = useState(0);
  const [filter, setFilter] = useState('all'); // all | audiobook | text

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/trash`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(Array.isArray(data?.trash) ? data.trash : []);
      setPurgedCount(Number(data?.purged_expired || 0));
    } catch (e) {
      toast.error(`Failed to load trash: ${e?.message || 'unknown'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (row) => {
    const label = row?.series?.title || row?.series_id;
    if (!window.confirm(`Restore "${label}"? It will reappear in the live library with all its ${row?.chapter_count || 0} chapter(s)/episode(s).`)) return;
    setBusyId(row.trash_id);
    const tid = toast.loading('Restoring…');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/trash/${row.trash_id}/restore`, { method: 'POST' });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      toast.success(`✓ Restored "${label}"`, { id: tid });
      setRows((prev) => prev.filter((x) => x.trash_id !== row.trash_id));
    } catch (e) {
      toast.error(`Restore failed: ${e?.message || 'unknown'}`, { id: tid });
    } finally {
      setBusyId('');
    }
  };

  const handlePurge = async (row) => {
    const label = row?.series?.title || row?.series_id;
    if (!window.confirm(`Permanently delete "${label}" NOW? This bypasses the 30-day recovery window and CANNOT be undone. Audio files on disk will also be removed.`)) return;
    const typed = window.prompt(`Type DELETE to confirm permanently removing "${label}".`);
    if (typed !== 'DELETE') {
      toast('Cancelled — trash entry kept.');
      return;
    }
    setBusyId(row.trash_id);
    const tid = toast.loading('Purging…');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/trash/${row.trash_id}`, { method: 'DELETE' });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      toast.success(`✓ Purged "${label}"`, { id: tid });
      setRows((prev) => prev.filter((x) => x.trash_id !== row.trash_id));
    } catch (e) {
      toast.error(`Purge failed: ${e?.message || 'unknown'}`, { id: tid });
    } finally {
      setBusyId('');
    }
  };

  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((r) => r.kind === filter);
  }, [rows, filter]);

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="admin-trash-tab">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle className="text-white flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-rose-300" />
            Recently Deleted (Trash)
          </CardTitle>
          <p className="text-xs text-slate-400 mt-1">
            Series deletions are held here for <strong>30 days</strong> before auto-purge. Restore rehydrates the series and every chapter/episode.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'audiobook', 'text'].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${filter === k ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-slate-700/40 border-slate-600 text-slate-300 hover:bg-slate-700'}`}
              data-testid={`admin-trash-filter-${k}`}
            >
              {k === 'all' ? 'All' : k === 'audiobook' ? 'Audiobook' : 'Text/Book'}
            </button>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="admin-trash-refresh"
            className="border-slate-600 text-slate-200"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {purgedCount > 0 && (
          <div className="text-[11px] text-amber-300/80 mb-3 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" />
            Auto-purged {purgedCount} expired entr{purgedCount === 1 ? 'y' : 'ies'} on load.
          </div>
        )}
        {loading && rows.length === 0 ? (
          <div className="text-slate-400 text-sm py-8 text-center">Loading trash…</div>
        ) : filtered.length === 0 ? (
          <div className="text-slate-400 text-sm py-8 text-center" data-testid="admin-trash-empty">
            Trash is empty. Deleted series will appear here for 30 days.
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((row) => {
              const s = row.series || {};
              const remaining = daysUntil(row.expires_at);
              const isBusy = busyId === row.trash_id;
              const actor = row.deleted_by_user || {};
              return (
                <div
                  key={row.trash_id}
                  className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 flex items-start gap-3"
                  data-testid={`admin-trash-row-${row.trash_id}`}
                >
                  <div className="flex-shrink-0 mt-0.5"><KindIcon kind={row.kind} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white text-sm font-medium truncate">{s.title || '(untitled)'}</span>
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-700/70 text-slate-300">{row.kind}</span>
                      <span className="text-[10px] text-slate-400">· {row.chapter_count || 0} chapter{row.chapter_count === 1 ? '' : 's'}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      Deleted {fmtDate(row.deleted_at)}{' '}
                      {actor.email && (
                        <>by <span className="text-slate-300">{actor.name || actor.email}</span> <span className="text-slate-500">({row.actor_role})</span></>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      series_id: <code className="text-slate-400">{row.series_id}</code>
                      {remaining !== null && (
                        <> · <span className={remaining <= 3 ? 'text-rose-300' : 'text-emerald-300'}>{remaining} day{remaining === 1 ? '' : 's'} until auto-purge</span></>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(row)}
                      disabled={isBusy}
                      className="border-emerald-700 text-emerald-200 hover:bg-emerald-900/30"
                      data-testid={`admin-trash-restore-${row.trash_id}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />
                      Restore
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePurge(row)}
                      disabled={isBusy}
                      className="border-rose-700 text-rose-200 hover:bg-rose-900/30"
                      data-testid={`admin-trash-purge-${row.trash_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminTrashTab;
